import { Component, OnInit, Input, OnDestroy, EventEmitter, Output } from '@angular/core';
import { CorrectionModel, DataCorrectionArrayEvent, DataCorrectionArrayEventOperation, DataCorrectionInstance } from '../../../../models/dataCorrection/dataCorrection.model';
import { Subscription } from 'rxjs';
import { getPropertyValue } from "../../../../common/models/data-manipulation-functions";
import { AbstractControl, FormGroup } from '@angular/forms';
import { DynamicDialogService } from '../../../../core/services/dynamic-dialog.service';
import { Dialog, DialogButton } from '../../../../core/models/dialog.model';
@Component({
    selector: 'data-correction-input',
    templateUrl: './data-correction-input.component.html',
    styleUrls: ['./data-correction-input.component.scss'],
    standalone: false
})
export class DataCorrectionInputComponent implements OnInit, OnDestroy {

    showInitialValues: { [id: string]: boolean; } = {};

    subscriptions: Subscription = new Subscription();
    @Input() isInReviewMode: boolean;
    @Input() showOnlyAlteredFields: boolean;
    @Input() isArrayEntry: boolean = false;
    @Input() formGroup: FormGroup;
	@Input() correctionData: CorrectionModel;
	@Input() parentModel: CorrectionModel;
	@Input() correctionDataInstance: DataCorrectionInstance; 


	get itemIsAdded(): boolean {
		return this._itemIsAdded;
	}

	set itemIsAdded(value: boolean) {
		this._itemIsAdded = value;		
	}

	get itemIsRemoved(): boolean {	
		return this._itemIsRemoved;
	}

	set itemIsRemoved(value: boolean) {
		this._itemIsRemoved = value;
	}

	private _itemIsAdded: boolean = false;
	private _itemIsRemoved: boolean = false;

    @Output() correctionDataArrayChanged = new EventEmitter<DataCorrectionArrayEvent>();

    constructor(public dialogService: DynamicDialogService) {
    }

    removeArrayItemDialog: Dialog = new Dialog("Are you sure you want to remove an entry from this array?",
        [
            new DialogButton("Continue", true, () => this.onRemoveArrayItemConfirm()),
            new DialogButton("Cancel", false, () => { }),
        ]);

    addArrayItemDialog: Dialog = new Dialog("Are you sure you want to add an entry to this array?",
        [
            new DialogButton("Continue", true, () => this.onAddArrayItemConfirm()),
            new DialogButton("Cancel", false, () => { }),
        ]);

	ngOnInit() {
		if (this.correctionData != null && this.correctionData.childCorrectionModels != null && this.correctionData.childCorrectionModels.length > 0) {
			let removedProp = this.correctionData.childCorrectionModels.find(m => m.propertyDisplayName.startsWith('Item Is Deleted'));
			let addedProp = this.correctionData.childCorrectionModels.find(m => m.propertyDisplayName.startsWith('Item Is Added'));
			this._itemIsAdded = addedProp != null && addedProp.propertyValue === true;
			
			this._itemIsRemoved = removedProp != null && removedProp.propertyValue === true;
		}

		if (this.isArrayEntry) {
			let ctrl = this.formGroup.controls[this.correctionData.propertyId];

			if (ctrl != null && (ctrl.value == null || ctrl.value.length == 0)) {
				this._itemIsRemoved = true;
			}
		}

		if (this.isSimpleType() && this.formGroup && this.correctionData) {
			const ctrl = this.formGroup.controls[this.correctionData.propertyId];
			if (ctrl) {
				if (this.correctionData.propertyAllowEdit && !this.isInReviewMode) {
					ctrl.enable();
				} else {
					ctrl.disable();
				}
			}
		}
    }

    ngOnDestroy() {
        if (this.subscriptions) this.subscriptions.unsubscribe();
	}



    displayField() {
        let hasBeenEdited = this.fieldHasBeenEdited();
        let displayField = !this.isInReviewMode || (this.isInReviewMode && hasBeenEdited) || !this.showOnlyAlteredFields;

        return displayField;
    }

    isSimpleType(): boolean {
        switch (this.correctionData.propertyType) {
            case "object":
            case "array":
                return false;
            default: return true;
        }
    }

	fieldHasBeenEdited() {
        let originalValue = getPropertyValue(this.correctionDataInstance.originalObject, this.correctionData.propertyDataSource, "");
        let updatedValue = getPropertyValue(this.correctionDataInstance.updatedObject, this.correctionData.propertyDataSource, "");

        //a null parent object (property never set) and an existing object with an empty/null leaf value
        //both represent "no value" - normalize both to "" so only genuine value changes are flagged
        const normalize = (value: any) => value === null || value === undefined ? "" : value;

        let hasBeenEdited = normalize(originalValue) !== normalize(updatedValue);
        return hasBeenEdited;
    }

    getFieldOriginalValue(fieldDataSource: string) {
        let originalValue = getPropertyValue(this.correctionDataInstance.originalObject, fieldDataSource, "N/A");

        return originalValue !== undefined && originalValue !== null && originalValue !== "" ? originalValue.toString() : "N/A";
    }

    toggleShowInitialValue(dataSource: string, value: boolean): void {
        this.showInitialValues[dataSource] = value;
    }

    getChildFormGroup(): AbstractControl {
        //console.log("Trying to fetch child formGroup for " + this.correctionData.propertyId);
        //Apparently formGroup.get can't retrieve items if there are periods in the name
        let childFormGroup = this.formGroup.controls[this.correctionData.propertyId];

        return childFormGroup;
    }

    onAddArrayItemConfirm() {
        let childFormGroup = this.getChildFormGroup();
        let correctionDataArrayChangedEvent: DataCorrectionArrayEvent = {
            operation: DataCorrectionArrayEventOperation.AddNewItem,
			correctionModel: this.correctionData
		};
        this.correctionDataArrayChanged.emit(correctionDataArrayChangedEvent);
    }

    onAddArrayEntryClick() {
        this.addArrayItemDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.addArrayItemDialog);
    }


    onRemoveArrayEntryClick() {
        this.removeArrayItemDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.removeArrayItemDialog);
    }

    onRemoveArrayItemConfirm() {
        let correctionDataArrayChangedEvent: DataCorrectionArrayEvent = {
            operation: DataCorrectionArrayEventOperation.RemoveCurrentItem,
			correctionModel: this.correctionData
		};
		this.itemIsRemoved = true;
		if (this.itemIsAdded) {
			this.itemIsAdded = false;
		}
        this.correctionDataArrayChanged.emit(correctionDataArrayChangedEvent);
    }

    //Pass it on up the chain
	onCorrectionDataArrayChanged(correctionDataArrayChangedEvent: DataCorrectionArrayEvent) {
        this.correctionDataArrayChanged.emit(correctionDataArrayChangedEvent);
	}

	undoRemove(): void {
		this._itemIsRemoved = false;
		if (this.correctionData.childCorrectionModels != null) {
			let removedProp = this.correctionData.childCorrectionModels.find(m => m.propertyDisplayName.startsWith('Item Is Deleted'));
			if (removedProp != null) {
				removedProp.propertyValue = false;
			}
		}
		
	}
}
